import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react'
import { assertNever } from '@/lib/assert-never'
import type { CoachingClient } from '../../problems/types'
import {
  COACHING_TAB_IDS,
  COACHING_TAB_LABEL,
  type CoachingTabId,
  hashTab,
  initialActiveTab,
  persistActiveTab,
} from './_lib/active-tab'
import { CurriculumTab } from './curriculum/curriculum-tab'
import {
  PersistedCurriculumBoundary,
  type UseCurriculumPersistenceApi,
} from './curriculum/persisted-curriculum'
import { ClientHeader } from './header/client-header'
import { StatStrip } from './header/stat-strip'
import {
  AdminOverviewProvider,
  useAdminOverview,
} from './overview/admin-overview-provider'
import { OverviewTab } from './overview/overview-tab'
import { PersistedReviewBoundary } from './review/persisted-review'
import type { UseReviewPersistenceApi } from './review/review-slice'
import { ReviewTab } from './review/review-tab'
import { RoadmapTab } from './roadmap/roadmap-tab'
import { RolloutStatus } from './rollout-status'
import { CoachingDashboardMockProvider } from './store'

// Derived, never restated: membership and bar order come from
// COACHING_TAB_IDS and the labels from its total label map, so a new tab
// cannot reach the id union without also reaching the bar.
const TABS: { id: CoachingTabId; label: string }[] = COACHING_TAB_IDS.map(
  (id) => ({ id, label: COACHING_TAB_LABEL[id] }),
)

/**
 * Coaching Dashboard v2 (spec §5, §6). Wraps everything in the mock store
 * provider so switching tabs never loses state. The `coaching-dashboard` class
 * scopes the `--cd-*` design tokens, which resolve to light or dark values based
 * on the app's global theme.
 */
export function CoachingDashboard(props: {
  client: CoachingClient
  clientId: string
  curriculumMode?: 'mock' | 'persisted'
  useCurriculumPersistenceApi?: UseCurriculumPersistenceApi
  /**
   * `'mock'` keeps the Review tab on the §8.3 fixture seeds — the mock-first
   * shell, which renders with no tRPC provider at all. `'persisted'` (the
   * default, like the curriculum lane) reads the client's real review queue
   * through `coachingV2.reviews.*`.
   */
  reviewMode?: 'mock' | 'persisted'
  useReviewPersistenceApi?: UseReviewPersistenceApi
  /**
   * Renders the real tRPC-backed rollout & delivery panel. Off by default so
   * the mock-first shell stays renderable without a tRPC provider; the live
   * client-detail route opts in.
   */
  showRolloutStatus?: boolean
}): ReactNode {
  const { client, clientId, useCurriculumPersistenceApi } = props
  const curriculumMode = props.curriculumMode ?? 'persisted'
  const reviewMode = props.reviewMode ?? 'persisted'

  return (
    // `key` on the provider forces the whole subtree — provider state (mock
    // store slices) AND shell tab state — to remount fresh per client.
    <CoachingDashboardMockProvider key={clientId}>
      <AdminOverviewProvider clientId={clientId}>
        <CoachingDashboardShell
          client={client}
          clientId={clientId}
          curriculumMode={curriculumMode}
          useCurriculumPersistenceApi={useCurriculumPersistenceApi}
          reviewMode={reviewMode}
          useReviewPersistenceApi={props.useReviewPersistenceApi}
          showRolloutStatus={props.showRolloutStatus ?? false}
        />
      </AdminOverviewProvider>
    </CoachingDashboardMockProvider>
  )
}

/**
 * Scopes the live review lane over everything that reads it — today the Review
 * tab body, which sits below the tab bar the lane also wraps.
 *
 * The wrapper is chosen by `mode`, which is fixed for the mount, and never by
 * which tab is open: swapping the element type here on a tab switch would
 * remount the tab bar underneath the coach's fingers. Whether the tab is the
 * active one is passed *down* instead, where it gates the queries.
 */
function ReviewLane(props: {
  active: boolean
  mode: 'mock' | 'persisted'
  clientId: string
  useApi?: UseReviewPersistenceApi
  children: ReactNode
}): ReactNode {
  if (props.mode === 'mock') return props.children
  return (
    <PersistedReviewBoundary
      clientId={props.clientId}
      active={props.active}
      useApi={props.useApi}
    >
      {props.children}
    </PersistedReviewBoundary>
  )
}

function CoachingDashboardShell(props: {
  client: CoachingClient
  clientId: string
  curriculumMode: 'mock' | 'persisted'
  useCurriculumPersistenceApi?: UseCurriculumPersistenceApi
  reviewMode: 'mock' | 'persisted'
  useReviewPersistenceApi?: UseReviewPersistenceApi
  showRolloutStatus: boolean
}): ReactNode {
  const {
    client,
    clientId,
    curriculumMode,
    useCurriculumPersistenceApi,
    reviewMode,
    useReviewPersistenceApi,
    showRolloutStatus,
  } = props
  const [activeTab, setActiveTab] = useState<CoachingTabId>(initialActiveTab)
  // One effect rather than a write beside each `setActiveTab` — the tab bar,
  // the arrow-key handler, and both `onOpenReview` branches all change the tab,
  // and a future fifth caller should not have to remember to persist. Running
  // on mount is deliberate: it makes a `#curriculum` deep link the remembered
  // tab, and rewrites the fragment to match a tab restored from storage.
  useEffect(() => {
    persistActiveTab(activeTab)
  }, [activeTab])
  // The fragment is a real input, not just an output: the shell does not
  // remount on a same-document hash change, so without this a coach editing
  // the hash by hand (or stepping Back over an entry the browser made for
  // them) would see the URL and the panel disagree. `persistActiveTab` uses
  // `replaceState`, which fires neither event, so this cannot feed back.
  useEffect(() => {
    const syncFromHash = (): void => {
      const linked = hashTab()
      if (linked) setActiveTab(linked)
    }
    window.addEventListener('hashchange', syncFromHash)
    return () => window.removeEventListener('hashchange', syncFromHash)
  }, [])
  const tabGroupId = useId()
  // Review-badge count is a live overview fact (spec §6.3); null while loading /
  // when the engagement can't be resolved renders no badge (honest, never faked).
  const { overview } = useAdminOverview()
  const reviewBadgeCount = overview?.reviewBadgeCount ?? 0

  const tabId = (id: CoachingTabId): string => `${tabGroupId}-tab-${id}`
  const panelId = (id: CoachingTabId): string => `${tabGroupId}-panel-${id}`

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentId: CoachingTabId,
  ): void => {
    const currentIndex = TABS.findIndex((tab) => tab.id === currentId)
    let nextIndex: number

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % TABS.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + TABS.length) % TABS.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = TABS.length - 1
    } else {
      return
    }

    event.preventDefault()
    const nextTab = TABS[nextIndex]
    setActiveTab(nextTab.id)
    document.getElementById(tabId(nextTab.id))?.focus()
  }

  // A switch rather than an `activeTab === … &&` chain: `activeTab` is seeded
  // from outside the app (a link's fragment, a stored preference), so a tab id
  // with no arm here would render an empty, correctly-labelled panel — a blank
  // dashboard that no type check would have caught.
  const renderTabPanel = (tab: CoachingTabId): ReactNode => {
    switch (tab) {
      case 'overview':
        return <OverviewTab clientId={clientId} />
      case 'review':
        return <ReviewTab />
      case 'curriculum': {
        const curriculum = (
          <CurriculumTab onOpenReview={() => setActiveTab('review')} />
        )
        return curriculumMode === 'mock' ? (
          curriculum
        ) : (
          <PersistedCurriculumBoundary
            clientId={clientId}
            useApi={useCurriculumPersistenceApi}
          >
            {curriculum}
          </PersistedCurriculumBoundary>
        )
      }
      case 'roadmap':
        return <RoadmapTab />
      default:
        return assertNever(tab, 'Unhandled coaching dashboard tab')
    }
  }

  return (
    <div className="coaching-dashboard mx-auto flex max-w-[1460px] flex-col gap-4 bg-(--cd-bg) px-6 py-6 text-(--cd-text)">
      <ClientHeader client={client} />
      <StatStrip />
      {showRolloutStatus && <RolloutStatus clientId={clientId} />}

      <ReviewLane
        active={activeTab === 'review'}
        mode={reviewMode}
        clientId={clientId}
        useApi={useReviewPersistenceApi}
      >
        <div className="flex items-center justify-between gap-3">
          <div
            role="tablist"
            aria-label="Coaching dashboard sections"
            aria-orientation="horizontal"
            className="inline-flex items-center gap-1 rounded-lg bg-(--cd-card) p-1"
          >
            {TABS.map((tab) => {
              const isActive = tab.id === activeTab
              const accessibleLabel =
                tab.id === 'review' && reviewBadgeCount > 0
                  ? `Review, ${reviewBadgeCount} awaiting ${reviewBadgeCount === 1 ? 'review' : 'reviews'}`
                  : tab.label
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={tabId(tab.id)}
                  aria-label={accessibleLabel}
                  aria-selected={isActive}
                  // Only the active panel exists in the DOM; aria-controls on
                  // inactive tabs would reference nonexistent ids (axe failure).
                  aria-controls={isActive ? panelId(tab.id) : undefined}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(event) => handleTabKeyDown(event, tab.id)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-(--cd-tab-active) text-(--cd-text)'
                      : 'text-(--cd-text-muted) hover:text-(--cd-text)'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.id === 'review' && reviewBadgeCount > 0 && (
                    <span
                      aria-hidden="true"
                      data-testid="review-tab-badge"
                      className="rounded-full bg-(--cd-green) px-1.5 py-0.5 text-[10px] font-bold text-(--cd-accent-contrast)"
                    >
                      {reviewBadgeCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div
          role="tabpanel"
          id={panelId(activeTab)}
          aria-labelledby={tabId(activeTab)}
          className="min-w-0"
        >
          {renderTabPanel(activeTab)}
        </div>
      </ReviewLane>
    </div>
  )
}
